import { FREE_DAYS, type ProgressState } from "../shared";
import { FIRST_DAY } from "./calendar";
import { getWeekNumber, isWeekReleased } from "./week";

export type DayStatus =
  | "locked"
  | "paywalled"
  | "ahead"
  | "catch-up"
  | "current"
  | "completed";

export interface DayState {
  day: number;
  status: DayStatus;
  /** This day's week has started, so its sheet exists for the learner. */
  isReleased: boolean;
  /** Released and paid for: the sheet can actually be opened and printed. */
  isOpen: boolean;
  isCurrent: boolean;
  isCompleted: boolean;
}

type CompletedDaysInput = ProgressState | readonly number[];

function completedDaySet(completed: CompletedDaysInput): ReadonlySet<number> {
  return new Set(
    Array.isArray(completed)
      ? completed
      : (completed as ProgressState).completedDays,
  );
}

/** The first week is open to everyone; every later week needs the unlock. */
export function isDayPaid(day: number, unlocked: boolean): boolean {
  return unlocked || day <= FREE_DAYS;
}

/**
 * A day arrives with its week rather than on its own morning, so a whole week
 * of sheets can be printed in one go and stuck on the fridge.
 */
export function isDayReleased(day: number, currentDay: number): boolean {
  return day >= FIRST_DAY && isWeekReleased(getWeekNumber(day), currentDay);
}

export function isDayOpen(
  day: number,
  currentDay: number,
  unlocked: boolean,
): boolean {
  return isDayReleased(day, currentDay) && isDayPaid(day, unlocked);
}

/**
 * The single display state for a day. A day later in this week is open and
 * printable but cannot be ticked off yet, so a stale completion from further
 * down the week never shows as done.
 */
export function getDayState(
  day: number,
  currentDay: number,
  completed: CompletedDaysInput = [],
  unlocked = false,
): DayState {
  const isReleased = isDayReleased(day, currentDay);
  const open = isDayOpen(day, currentDay, unlocked);
  const isCompleted =
    open && day <= currentDay && completedDaySet(completed).has(day);

  let status: DayStatus;
  if (!isReleased) {
    status = "locked";
  } else if (!open) {
    status = "paywalled";
  } else if (isCompleted) {
    status = "completed";
  } else if (day === currentDay) {
    status = "current";
  } else if (day < currentDay) {
    status = "catch-up";
  } else {
    status = "ahead";
  }

  return {
    day,
    status,
    isReleased,
    isOpen: open,
    isCurrent: status === "current",
    isCompleted,
  };
}

export function getDayStates(
  currentDay: number,
  totalDays: number,
  completed: CompletedDaysInput = [],
  unlocked = false,
): DayState[] {
  return Array.from({ length: Math.max(0, totalDays) }, (_, index) =>
    getDayState(index + FIRST_DAY, currentDay, completed, unlocked),
  );
}

export function countCompleted(
  completed: CompletedDaysInput,
  totalDays: number,
): number {
  return [...completedDaySet(completed)].filter(
    (day) => day >= FIRST_DAY && day <= totalDays,
  ).length;
}
